import { useState, useEffect, useCallback } from 'react';
import DatabaseService from '../services/databaseService';

// Custom hook for database operations
export const useDatabase = () => {
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get all published books
  const fetchBooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await DatabaseService.getPublishedBooks();
      setBooks(data);
    } catch (err) {
      setError('Error al cargar los libros');
      console.error('Error fetching books:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get books by category
  const fetchBooksByCategory = useCallback(async (category) => {
    setLoading(true);
    setError(null);
    try {
      const data = await DatabaseService.getBooksByCategory(category);
      setBooks(data);
    } catch (err) {
      setError('Error al cargar los libros por categoría');
      console.error('Error fetching books by category:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search books
  const searchBooks = useCallback(async (query) => {
    if (!query.trim()) {
      await fetchBooks();
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await DatabaseService.searchBooks(query);
      setBooks(data);
    } catch (err) {
      setError('Error al buscar libros');
      console.error('Error searching books:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchBooks]);

  // Get categories
  const fetchCategories = useCallback(async () => {
    try {
      const data = await DatabaseService.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  // Get authors
  const fetchAuthors = useCallback(async () => {
    try {
      const data = await DatabaseService.getAuthors();
      setAuthors(data);
    } catch (err) {
      console.error('Error fetching authors:', err);
    }
  }, []);

  // Get languages
  const fetchLanguages = useCallback(async () => {
    try {
      const data = await DatabaseService.getLanguages();
      setLanguages(data);
    } catch (err) {
      console.error('Error fetching languages:', err);
    }
  }, []);

  // Toggle favorite
  const toggleFavorite = useCallback(async (bookId, isFavorite) => {
    try {
      const success = await DatabaseService.toggleFavorite(bookId, isFavorite);
      if (success) {
        // Update the book in the local state
        setBooks(prevBooks => 
          prevBooks.map(book => 
            book.id_product === bookId 
              ? { ...book, favorite_product: isFavorite }
              : book
          )
        );
      }
      return success;
    } catch (err) {
      console.error('Error toggling favorite:', err);
      return false;
    }
  }, []);

  // Get books on sale
  const fetchBooksOnSale = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await DatabaseService.getBooksOnSale();
      setBooks(data);
    } catch (err) {
      setError('Error al cargar los libros en oferta');
      console.error('Error fetching books on sale:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get favorite books
  const fetchFavoriteBooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await DatabaseService.getFavoriteBooks();
      setBooks(data);
    } catch (err) {
      setError('Error al cargar los libros favoritos');
      console.error('Error fetching favorite books:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get books by price range
  const fetchBooksByPriceRange = useCallback(async (minPrice, maxPrice) => {
    setLoading(true);
    setError(null);
    try {
      const data = await DatabaseService.getBooksByPriceRange(minPrice, maxPrice);
      setBooks(data);
    } catch (err) {
      setError('Error al cargar los libros por rango de precio');
      console.error('Error fetching books by price range:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get books by language
  const fetchBooksByLanguage = useCallback(async (language) => {
    setLoading(true);
    setError(null);
    try {
      const data = await DatabaseService.getBooksByLanguage(language);
      setBooks(data);
    } catch (err) {
      setError('Error al cargar los libros por idioma');
      console.error('Error fetching books by language:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sort books
  const sortBooks = useCallback(async (sortBy) => {
    setLoading(true);
    setError(null);
    try {
      const data = await DatabaseService.sortBooks(sortBy);
      setBooks(data);
    } catch (err) {
      setError('Error al ordenar los libros');
      console.error('Error sorting books:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    fetchBooks();
    fetchCategories();
  }, [fetchBooks, fetchCategories]);

  // Get book by ID
  const fetchBookById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await DatabaseService.getBookById(id);
      return data;
    } catch (err) {
      setError('Error al cargar el libro');
      console.error('Error fetching book by ID:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    books,
    categories,
    authors,
    languages,
    loading,
    error,
    fetchBooks,
    fetchBooksByCategory,
    searchBooks,
    fetchCategories,
    fetchAuthors,
    fetchLanguages,
    toggleFavorite,
    fetchBooksOnSale,
    fetchFavoriteBooks,
    fetchBooksByPriceRange,
    fetchBooksByLanguage,
    sortBooks,
    fetchBookById,
    clearError: () => setError(null)
  };
};

export default useDatabase;

